/**
 * Slice 49 — sends start / finish stamps that were taken with no signal.
 * Renders nothing. The cleaner never has to do anything: when the phone is back in signal
 * (or the app is opened again) the waiting stamps are written to their jobs unchanged.
 */
import { useCallback, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useStartCleaningTimer } from "@/hooks/useCurrentCleaningTask";
import { useMarkTaskComplete, useMarkTaskCompleteAsOwnerOrTeam } from "@/hooks/useCleaner";
import { flushOutbox, getOutboxEntries, type OutboxEntry } from "@/lib/stampOutbox";

const RETRY_MS = 30_000;

export function StampOutboxSync() {
  const { user } = useAuth();
  const start = useStartCleaningTimer();
  const finishCleaner = useMarkTaskComplete();
  const finishTeam = useMarkTaskCompleteAsOwnerOrTeam();
  const busy = useRef(false);

  const startRef = useRef(start.mutateAsync);
  const finishCleanerRef = useRef(finishCleaner.mutateAsync);
  const finishTeamRef = useRef(finishTeam.mutateAsync);
  startRef.current = start.mutateAsync;
  finishCleanerRef.current = finishCleaner.mutateAsync;
  finishTeamRef.current = finishTeam.mutateAsync;

  const flush = useCallback(async () => {
    if (busy.current || !user || getOutboxEntries().length === 0) return;
    if (typeof navigator !== "undefined" && navigator.onLine === false) return;
    busy.current = true;
    try {
      await flushOutbox((entry: OutboxEntry) => {
        if (entry.kind === "start") {
          const extra = (entry.extra ?? {}) as {
            extraDirty?: boolean;
            extraDirtyNotes?: string | null;
            earlyCleaningStarted?: boolean;
          };
          return startRef.current({
            taskId: entry.taskId,
            propertyId: entry.propertyId,
            extraDirty: extra.extraDirty,
            extraDirtyNotes: extra.extraDirtyNotes,
            earlyCleaningStarted: extra.earlyCleaningStarted,
            replayStamp: entry.stamp,
          });
        }
        if (entry.kind === "finish_cleaner") {
          return finishCleanerRef.current({
            taskId: entry.taskId,
            propertyId: entry.propertyId,
            replayStamp: entry.stamp,
          });
        }
        return finishTeamRef.current({
          taskId: entry.taskId,
          propertyId: entry.propertyId,
          replayStamp: entry.stamp,
        });
      });
    } finally {
      busy.current = false;
    }
  }, [user]);

  useEffect(() => {
    void flush();
    const onOnline = () => void flush();
    const onVisible = () => {
      if (document.visibilityState === "visible") void flush();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => void flush(), RETRY_MS);
    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [flush]);

  return null;
}
